import React, { useCallback, useEffect, useRef, useState } from 'react';

import useDebouncedCallback from '@exp/hooks/useDebouncedCallback';
import useOnScreen from '@exp/hooks/useOnScreen';

import { InitialFormModes } from '../constants/constants';
import { ModeConfig } from '../constants/modes';
import { selectConfig, selectIsHeaderVisible, setFormMode } from '../redux/context';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import useMutationObserver from '../utilities/useMutationObserver';
import ExpertVoiceDarkLogo from '../images/EVLogoNoTagline.svg';
import { TemplateProps } from './types';
import Backdrop from './Backdrop';

import './ModalLayoutV3.less';

const CONTENT_MAX_WIDTH = 1400;
const CONTENT_MIN_HEIGHT = 600;

const ModalLayoutV3 = React.forwardRef<HTMLDivElement, TemplateProps>(({ children = null, onClose = null }, ref) => {
  const [scale, setScale] = useState(1);
  const contentBlock = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const isHeaderVisible = useAppSelector(selectIsHeaderVisible);
  const { toggles, theme }: ModeConfig = useAppSelector(selectConfig);

  // vets stuff. can be removed if that layout is not retained
  const heightHandler = () => {
    window.setTimeout(() => {
      if (contentBlock?.current != null) {
        const dimensions = contentBlock.current.getBoundingClientRect();
        document.documentElement.style.setProperty('--content-height', `${dimensions.height}px`);
      }
    }, 300);
  };

  const formRef = useRef<HTMLFormElement>(null);
  const formInView = useOnScreen(formRef, { threshold: 1.0 });
  const scrollingToForm = useRef(false);

  const handleFormScroll = useCallback(
    (fm) => {
      formRef.current = window.document.getElementById('exp-onb-initial-form') as HTMLFormElement;
      if (formRef?.current) {
        scrollingToForm.current = true;
        formRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      }

      dispatch(setFormMode(fm));
    },
    [dispatch],
  );

  useEffect(() => {
    // Auto-focus the username (or firstName) field if the form is being scrolled into view
    if (formInView && scrollingToForm?.current) {
      let input = window.document.getElementById('exp-onb-username');
      if (!input) {
        input = window.document.getElementById('exp-onb-first-name');
      }

      input?.focus();
      scrollingToForm.current = false;
    }
  }, [formInView]);

  useMutationObserver(contentBlock, heightHandler);

  useEffect(() => {
    window.addEventListener('resize', heightHandler);
    window.setTimeout(heightHandler, 600);
    return () => {
      window.removeEventListener('resize', heightHandler);
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      // At sizes wider than XL, we scale the content if it will fit vertically
      const screenW = window.innerWidth;
      const maxScale = window.innerHeight / CONTENT_MIN_HEIGHT;
      setScale(Math.min(screenW / CONTENT_MAX_WIDTH, 1.5, maxScale, 1));
    };
    handler();
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);

  const [scrollTop, setScrollTop] = useState(0);
  const debouncedScroll = useDebouncedCallback(
    () => {
      setScrollTop(window.scrollY);
    },
    50,
    [],
  );
  useEffect(() => {
    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
    };
  }, [debouncedScroll]);

  // This allows the user to use the back button to get the sign-up to pop up
  const historyUpdate = useCallback((e) => {
    e.preventDefault(e);
    window.history.pushState({ page: 'onboarding' }, '', '/sign-up');
    window.location.href = '/';
  }, []);

  return (
    <>
      {isHeaderVisible && (
        <div className="exp-modal-header" data-scroll={scrollTop}>
          <a className="header-logo" href="/" onClick={historyUpdate}>
            <img alt="ExpertVoice" className="exp-onb-initial-logo" src={ExpertVoiceDarkLogo} />
          </a>
          {theme === 'vets' ? (
            <>
              <div className="tagline">Where military service meets unmatched savings</div>
              <div className="header-actions">
                <button
                  className="btn-primary"
                  onClick={() => {
                    handleFormScroll(InitialFormModes.SIGN_UP);
                  }}
                  type="button"
                >
                  Join + Get $20
                </button>
              </div>
            </>
          ) : onClose && !toggles.preventClose ? (
            <button aria-label="Close" className="exp-modal-close btn-wp-reset" onClick={onClose} type="button">
              <i className="exp-ux-small exp-ux-close" />
            </button>
          ) : null}
        </div>
      )}
      <div className="transform-container" style={{ '--content-scale': scale }}>
        <Backdrop />
        <div className="exp-modal" data-theme="v3" ref={ref}>
          <div className="exp-modal-content">
            <div className="content-inset" ref={contentBlock}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

ModalLayoutV3.displayName = 'ModalLayoutV3';

export default ModalLayoutV3;
